

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, TextField, Button, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router-dom";

const getservicesApi = process.env.REACT_APP_GET_SERVICES;
const updateservicesApi = process.env.REACT_APP_UPDATE_SERVICES_PRICING;
const insertservicesApi = process.env.REACT_APP_INSERT_SERVICES_PRICING;
const addServiceApi = process.env.REACT_APP_ADD_SERVICE;

export default function ServicePricingChange() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newService, setNewService] = useState({ name: "", price: "" }); 

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getservicesApi);
      let data = res.data;
      if (data.length === 0) {
        await axios.post(insertservicesApi);
        data = await (await axios.get(getservicesApi)).data;
      }
      setServices(data);
    } catch (err) {
      console.error("Error fetching services:", err);
    }
    setLoading(false);
  };

  const handlePriceChange = (id, value) => {
    setServices((prev) =>
      prev.map((s) => (s.id === id ? { ...s, price: value } : s))
    );
  };

  const handleUpdateAllPrices = async () => {
    setConfirmOpen(true);
  };

  const confirmUpdate = async () => {
    setConfirmOpen(false);
    setUpdating(true);
    try {
      const updates = services.map((s) => ({
        id: s.id,
        price: parseFloat(s.price),
      }));
      await axios.put(updateservicesApi, updates);
      setDialogMessage("All prices updated successfully!");
      setDialogOpen(true);
    } catch (err) {
      console.error("Error updating prices:", err);
      setDialogMessage(" Failed to update prices!");
      setDialogOpen(true);
    } finally {
      setUpdating(false);
    }
  };

  // Add new service
  const handleAddService = async () => {
    if (!newService.name.trim()) {
      setDialogMessage("Please enter a service name");
      setDialogOpen(true);
      return;
    }
    try {
      const res = await axios.post(addServiceApi, {
        name: newService.name.trim(),
        price: parseFloat(newService.price || 0),
      });
      setDialogMessage("New service added successfully!");
      setDialogOpen(true);
      setNewService({ name: "", price: "" });
      fetchServices();
    } catch (err) {
      console.error("Error adding service:", err);
      setDialogMessage(
        err.response?.data?.detail || " Failed to add new service"
      );
      setDialogOpen(true);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, width: "90%", mx: "auto", p: 4, mt: 5 }}>
      <IconButton onClick={() => navigate(-1)}>
        <ArrowBackIosNewIcon />
      </IconButton>

      <Typography variant="h4" sx={{ mb: 3, justifyContent: "center" }}>
        Update Service Prices
      </Typography>

      {/*  Add New Service Form */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          label="Service Name"
          value={newService.name}
          onChange={(e) => setNewService({ ...newService, name: e.target.value })}
          size="small"
        />
        <TextField
          label="Price (₹)"
          type="number"
          value={newService.price}
          onChange={(e) => setNewService({ ...newService, price: e.target.value })}
          size="small"
        />
        <Button
          variant="contained"
          onClick={handleAddService}
          sx={{ backgroundColor: "#006D77", textTransform: "none" }}
        >
          Add Service
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxWidth: 700, width: "100%" }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#006D77" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff" }}>Service Name</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Price (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={s.price}
                        onChange={(e) => handlePriceChange(s.id, e.target.value)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Update All Button */}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              sx={{ backgroundColor: "#006D77", textTransform: "none" }}
              onClick={handleUpdateAllPrices}
              disabled={updating}
              startIcon={updating && <CircularProgress size={20} color="inherit" />}
            >
              {updating ? "Updating All..." : "Update Prices"}
            </Button>
          </Box>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to update all prices?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>No</Button>
          <Button onClick={confirmUpdate}>Yes</Button>
        </DialogActions>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Status</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
